import React from 'react';
import { ArcSocketContext, ChannelRequestEnum, ChannelResponseEnum } from '@src/core/socket';

interface UseSignalingOptions {
  serialNumber: string;
  isViewer: boolean;
  viewerId: string;
  peerConnections: React.MutableRefObject<RTCPeerConnection | null>;
}

export const useSignaling = (options: UseSignalingOptions) => {
  const { serialNumber, isViewer, viewerId, peerConnections } = options;
  const { arcSocket, subscribe, unsubscribe, readyState } = React.useContext(ArcSocketContext);

  const sendSignal = React.useCallback((command: string, data: any) => {
    const webSocket = arcSocket();
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) return;

    const payload: any = {
      type: ChannelRequestEnum.WEBRTC,
      command,
      data,
      serialNumber,
    };

    if (isViewer) {
      payload.viewerMode = 'sfu';
      payload.viewerId = viewerId;
    }

    webSocket.send(JSON.stringify(payload));
  }, [arcSocket, serialNumber, isViewer, viewerId]);

  const getAnswerFromDrone = React.useCallback(async (answer: RTCSessionDescriptionInit): Promise<void> => {
    if (peerConnections.current) {
      await peerConnections.current.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }, [peerConnections]);

  const getIceCandidateFromDrone = React.useCallback((remoteCandidate: any): void => {
    let candidateObj: RTCIceCandidateInit;
    try {
      candidateObj = JSON.parse(remoteCandidate);
      if (candidateObj.candidate) {
        candidateObj.candidate = candidateObj.candidate.trim();
      }
    } catch (e) {
      candidateObj = {
        candidate: remoteCandidate,
        sdpMid: '0',
        sdpMLineIndex: 0
      };
    }

    if (peerConnections.current) {
      peerConnections.current.addIceCandidate(new RTCIceCandidate(candidateObj))
        .catch(error => console.error('Error adding ICE candidate:', error));
    }
  }, [peerConnections]);

  React.useEffect(() => {
    const handleWEBRTC = (event: any) => {
      const answerCommand = isViewer
        ? ChannelResponseEnum.WEBRTC_SFU_ANSWER
        : ChannelResponseEnum.WEBRTC_ANSWER;
      const iceCandidateCommand = isViewer
        ? ChannelResponseEnum.WEBRTC_SFU_ICE_CANDIDATE
        : ChannelResponseEnum.WEBRTC_ICE_CANDIDATE;

      if (event.command === answerCommand) {
        getAnswerFromDrone({ type: 'answer', sdp: event.data });
      } else if (event.command === iceCandidateCommand) {
        getIceCandidateFromDrone(event.data);
      }
    };

    subscribe(ChannelResponseEnum.WEBRTC, handleWEBRTC);
    return () => {
      unsubscribe(ChannelResponseEnum.WEBRTC);
    };
  }, [subscribe, unsubscribe, isViewer, getAnswerFromDrone, getIceCandidateFromDrone]);

  return {
    arcSocket,
    readyState,
    sendSignal,
  };
};
